import { UFS } from '../domain/entities/content.entity';
import type { Institution } from '../domain/entities/content.entity';

/**
 * Os filtros da busca de provas (DESEN-41), como ficam na URL: texto vazio é
 * "sem filtro". A cadeia é **UF → Instituição → Ano → Caderno**:
 *
 * - a Instituição depende da UF; Ano e Caderno dependem da instituição;
 * - o Caderno não espera o Ano: sem ano, as opções são os cadernos de todos os
 *   anos da instituição;
 * - trocar um elo limpa o que depende dele, exceto o caderno ao trocar o ano,
 *   que fica se ainda existe nas opções do ano novo (ver `dropInvalid`).
 */
export interface ExamSearchFilters {
  uf: string;
  institutionId: string;
  year: string;
  variant: string;
}

export const EMPTY_EXAM_FILTERS: ExamSearchFilters = {
  uf: '',
  institutionId: '',
  year: '',
  variant: '',
};

export function hasExamFilter(f: ExamSearchFilters): boolean {
  return Object.values(f).some((v) => v !== '');
}

/** Trocar a UF zera o resto da cadeia. */
export function changeUf(uf: string): ExamSearchFilters {
  return { ...EMPTY_EXAM_FILTERS, uf };
}

export function changeInstitution(
  f: ExamSearchFilters,
  institutionId: string,
): ExamSearchFilters {
  return { ...f, institutionId, year: '', variant: '' };
}

/** O caderno fica: se não existir no ano novo, `dropInvalid` o tira. */
export function changeYear(
  f: ExamSearchFilters,
  year: string,
): ExamSearchFilters {
  return { ...f, year };
}

export function changeVariant(
  f: ExamSearchFilters,
  variant: string,
): ExamSearchFilters {
  return { ...f, variant };
}

/** O que a pessoa pode escolher, para conferir o que veio da URL. */
export interface ExamFilterContext {
  /** Todas as instituições conhecidas, inclusive arquivadas; `undefined` carregando. */
  institutionsById?: Map<string, Institution>;
  /** Opções do back para a instituição atual; `undefined` carregando ou sem instituição. */
  options?: { years: number[]; variants: string[] };
}

/**
 * Descarta em silêncio o que não pode estar ali: UF fora da lista, ano que não
 * é inteiro, instituição apagada ou de outra UF, ano e caderno fora das
 * opções, e o elo sem o elo de que depende. O que ainda carrega não é julgado:
 * só sai quando a resposta mostra que é inválido.
 */
export function dropInvalid(
  f: ExamSearchFilters,
  ctx: ExamFilterContext,
): ExamSearchFilters {
  let next = { ...f };
  if (next.uf && !(UFS as readonly string[]).includes(next.uf)) {
    next = { ...EMPTY_EXAM_FILTERS };
  }
  if (next.year && !/^\d{4}$/.test(next.year)) next.year = '';
  if (!next.uf) next = { ...next, institutionId: '', year: '', variant: '' };
  if (next.institutionId && ctx.institutionsById) {
    const inst = ctx.institutionsById.get(next.institutionId);
    if (!inst || inst.deletedAt || inst.uf !== next.uf) {
      next = { ...next, institutionId: '', year: '', variant: '' };
    }
  }
  if (!next.institutionId) next = { ...next, year: '', variant: '' };
  if (ctx.options) {
    if (next.year && !ctx.options.years.includes(Number(next.year))) {
      next.year = '';
    }
    if (next.variant && !ctx.options.variants.includes(next.variant)) {
      next.variant = '';
    }
  }
  return next;
}

export function sameFilters(a: ExamSearchFilters, b: ExamSearchFilters) {
  return (
    a.uf === b.uf &&
    a.institutionId === b.institutionId &&
    a.year === b.year &&
    a.variant === b.variant
  );
}
